import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  HostListener,
  OnChanges,
  SimpleChanges,
  AfterViewChecked,
  ViewChild,
  ElementRef,
  OnDestroy,
} from '@angular/core';

import { Subscription } from 'rxjs';
import { LanguageService } from '../../services/language/language.service';
import { environment } from 'src/environments/environment';
import {
  CLOSE,
  CONTACT_SOCIAL_PREFILL_MESSAGE,
  CONTACT_US,
  CONTACT_VIA_TELEGRAM,
  CONTACT_VIA_WHATSAPP,
} from 'src/app/modules/route-planner/constants/multi-language.constants';

export interface ContactInfo {
  title?: string;
  /** Section title from API (contacts-phone-title-{lang}) */
  phoneTitle?: string;
  phoneNumbers: string[];
  serviceHours: {
    weekdays: string;
    weekend: string;
    saturdayNight: string;
  };
  /** Section title from API (contacts-address-title-{lang}) */
  addressTitle?: string;
  address: string;
  /** Section title from API (contacts-email-title-{lang}) */
  emailTitle?: string;
  email: string;
  /** Section title from API (contacts-service-languages-title-{lang}) */
  serviceLanguagesTitle?: string;
  languages: string[];
  publicReceptionLink: string;
  /** URL from API (contacts-public-inquiries-link-url-{lang}) */
  publicReceptionUrl?: string;
  accessibilityCommitteeLink: string;
  /** URL from API (contacts-fine-appeals-link-url-{lang}) */
  complaintUrl?: string;
  /** Full URL from API (contacts-whatsapp-link-{lang}) */
  whatsappLink?: string;
  /** Full URL from API (contacts-telegram-link-{lang}) */
  telegramLink?: string;
}

@Component({
    selector: 'app-contact-view',
    imports: [],
    templateUrl: './contact.view.html',
    styleUrls: ['./contact.view.scss']
})
export class ContactViewComponent implements OnInit, OnChanges, AfterViewChecked, OnDestroy {
  @Input() isOpen = false;
  @Input() contactInfo?: ContactInfo;
  @Output() close = new EventEmitter<void>();
  @ViewChild('contactCloseBtn', { static: false }) contactCloseBtnRef!: ElementRef<HTMLButtonElement>;
  @ViewChild('contactDialog', { static: false }) contactDialogRef!: ElementRef<HTMLElement>;

  private languageSub?: Subscription;

  /** Element that had focus before the dialog opened; restored on close. */
  private previousFocus: HTMLElement | null = null;
  private focusClosePending = false;

  /** Shell UI only — section copy comes from @Input contactInfo (API). */
  modalTitle = CONTACT_US.hebrew;
  closeAriaLabel = CLOSE.hebrew;
  viaWhatsAppLabel = CONTACT_VIA_WHATSAPP.hebrew;
  viaTelegramLabel = CONTACT_VIA_TELEGRAM.hebrew;
  socialPrefillMessage = CONTACT_SOCIAL_PREFILL_MESSAGE.hebrew;

  constructor(private readonly languageService: LanguageService) {}

  ngOnInit(): void {
    this.languageSub = this.languageService.selectedLanguage$.subscribe((language) => {
      const key = language.language;
      this.modalTitle = CONTACT_US[key];
      this.closeAriaLabel = CLOSE[key];
      this.viaWhatsAppLabel = CONTACT_VIA_WHATSAPP[key];
      this.viaTelegramLabel = CONTACT_VIA_TELEGRAM[key];
      this.socialPrefillMessage = CONTACT_SOCIAL_PREFILL_MESSAGE[key];
    });
  }

  ngOnDestroy(): void {
    this.languageSub?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && this.isOpen) {
      this.previousFocus = (document.activeElement as HTMLElement) || null;
      if (this.previousFocus && !document.body.contains(this.previousFocus)) {
        this.previousFocus = null;
      }
      this.focusClosePending = true;
    }
  }

  ngAfterViewChecked(): void {
    if (this.focusClosePending && this.isOpen && this.contactCloseBtnRef?.nativeElement) {
      this.focusClosePending = false;
      this.contactCloseBtnRef.nativeElement.focus();
    }
  }

  onClose(): void {
    const toFocus = this.previousFocus;
    this.close.emit();
    if (toFocus && typeof toFocus.focus === 'function') {
      setTimeout(() => toFocus.focus(), 0);
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.isOpen) {
      this.onClose();
    }
  }

  onDialogKeydown(event: KeyboardEvent): void {
    if (!this.isOpen || event.key !== 'Tab') return;
    const dialog = this.contactDialogRef?.nativeElement;
    if (!dialog) return;
    const focusableSelector =
      'button:not([tabindex="-1"]), a[href], input:not([tabindex="-1"]), [tabindex="0"]';
    const focusables = Array.from(dialog.querySelectorAll<HTMLElement>(focusableSelector)).filter(
      (el) => window.getComputedStyle(el).display !== 'none' && el.offsetParent !== null
    );
    if (focusables.length === 0) return;
    const current = document.activeElement as HTMLElement;
    const currentIndex = focusables.indexOf(current);
    if (event.shiftKey) {
      if (currentIndex <= 0) {
        event.preventDefault();
        focusables[focusables.length - 1].focus();
      }
    } else {
      if (currentIndex === -1 || currentIndex >= focusables.length - 1) {
        event.preventDefault();
        focusables[0].focus();
      }
    }
  }

  openWhatsApp(): void {
    if (this.contactInfo?.whatsappLink) {
      window.open(this.contactInfo.whatsappLink, '_blank');
      return;
    }
    const phone = this.contactInfo?.phoneNumbers[0]?.replace(/[^0-9]/g, '') || '886831610';
    const message = encodeURIComponent(this.socialPrefillMessage);
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
  }

  openTelegram(): void {
    if (this.contactInfo?.telegramLink) {
      window.open(this.contactInfo.telegramLink, '_blank');
      return;
    }
    const username = 'mot_contact';
    const message = encodeURIComponent(this.socialPrefillMessage);
    window.open(`https://t.me/${username}?text=${message}`, '_blank');
  }

  getDirection(): string {
    return this.languageService.getSelectedLanguage()?.direction ?? 'rtl';
  }

  get publicReceptionUrl(): string {
    return this.contactInfo?.publicReceptionUrl ?? environment.publicReceptionUrl ?? '#';
  }

  get complaintUrl(): string {
    return this.contactInfo?.complaintUrl ?? environment.complaintUrl ?? '#';
  }
}
